import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast, useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import {
  PaperAirplaneIcon,
  HeartIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { Skeleton } from "@/components/ui/skeleton";

import {
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  HeartIcon as HeartIconSolid,
  PlusCircleIcon as PlusCircleIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from "@heroicons/react/24/solid";
import { UsersRound, Mail, Trash, Search, Check, Loader2 } from "lucide-react";
import { debounce } from "lodash";
import api from "@/config/axiosConfig";
import { UserSearchResult } from "@/types/user";
import { Label } from "@radix-ui/react-label";
import useAuthStore from "@/stores/user/authStore";
import useTrip from "@/stores/tripBuilder/useTrip";
import { useConnections } from "@/hooks/useConnections";
import { socialService } from "@/services/socialService";

interface ShareButtonProps {
  count: number;
  tripId: string;
  tripName: string;
  tripCreator: string;
  showCount?: boolean;
  className?: string;
  iconClassName?: string;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  trigger?: React.ReactNode;
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  count,
  tripId,
  tripName,
  tripCreator,
  showCount = false,
  className = "group relative w-6 h-6 flex items-center",
  iconClassName = "fa-lg",
  isModalOpen,
  setIsModalOpen,
  trigger,
}) => {
  const { user: currentUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState("");
  const { recordEngagement } = useTrip();
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState<Set<string>>(
    new Set()
  );
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const { data: connections, isLoading: isLoadingConnections } = useConnections(
    currentUser?.user_id || ""
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't handle clicks if they're on the dialog close button
      if (
        (event.target as Element)?.closest('[class*="DialogPrimitive-Close"]')
      ) {
        return;
      }

      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setSearchResults([]);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { toast } = useToast();

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.length < 3) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await api.social.get(
          `/search?q=${encodeURIComponent(query)}`
        );
        setSearchResults(response.data.results);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const toggleFriendSelection = (friendId: string) => {
    setSelectedFriends((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(friendId)) {
        newSet.delete(friendId);
      } else {
        newSet.add(friendId);
      }
      return newSet;
    });
  };

  const handleShare = async () => {
    if (selectedFriends.size === 0) {
      toast({
        title: "No friends selected",
        description: "Please select at least one friend to share with",
        variant: "destructive",
      });
      return;
    }

    if (!currentUser?.user_id) {
      return;
    }

    try {
      await socialService.shareTrip(
        tripId,
        currentUser?.user_id,
        Array.from(selectedFriends)
      );
      await recordEngagement(tripId, "share", {
        recepient_ids: Array.from(selectedFriends),
      });

      toast({
        title: "Trip shared successfully",
        description: `Shared with ${selectedFriends.size} friend${
          selectedFriends.size > 1 ? "s" : ""
        }`,
      });
      setIsModalOpen(false);
      setSelectedFriends(new Set());
    } catch (error) {
      console.error("Error sharing trip:", error);
      toast({
        title: "Error sharing trip",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog onOpenChange={setIsModalOpen}>
      <DialogTrigger>
        {trigger ? (
          trigger
        ) : (
          <div className={className}>
            <div className="relative">
              <PaperAirplaneIcon className="w-[19px] h-5 absolute transition-opacity duration-300 opacity-100 group-hover:opacity-0" />
              <PaperAirplaneIconSolid className="w-[19px] h-5 text-blue-600 transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
            </div>
          </div>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="space-y-2">
          <DialogTitle>Share this Trip</DialogTitle>
          <DialogDescription className="text-sm text-neutral-600 font-light">
            Send <span className="font-medium text-indigo-700">{tripName}</span>{" "}
            by{" "}
            <span className="font-medium text-indigo-700">{tripCreator}</span>{" "}
            to your friends
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Search Users Section */}
          <div className="space-y-2">
            <Label>Search Friends</Label>
            <div className="relative" ref={searchContainerRef}>
              <div className="relative h-10 w-full">
                <UsersRound className="absolute w-4 h-4 left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" />
                <Input
                  type="text"
                  placeholder="Search friends by name or email"
                  className="text-sm pl-10 pr-3 py-2 rounded-lg w-full shadow-sm placeholder:font-light"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>

              {/* Repositioned search results container */}
              {(searchResults.length > 0 ||
                (searchQuery.length >= 3 && isLoading)) && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg border border-gray-200 shadow-lg z-50">
                  <div className="max-h-[200px] overflow-y-auto py-1">
                    {isLoading ? (
                      // Loading skeletons
                      Array(3)
                        .fill(0)
                        .map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3"
                          >
                            <div className="flex items-center space-x-3">
                              <Skeleton className="w-8 h-8 rounded-full" />
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-[120px]" />
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Skeleton className="h-8 w-20" />
                              <Skeleton className="h-7 w-7 rounded-full" />
                            </div>
                          </div>
                        ))
                    ) : (
                      <>
                        {searchResults.map((user) => {
                          return (
                            <button
                              key={user.user_id}
                              className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition duration-200"
                              onClick={() => {
                                toggleFriendSelection(user.user_id);
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-neutral-100 overflow-hidden">
                                  {user.profile_image && (
                                    <img
                                      src={user.profile_image}
                                      alt={`${user.first_name}'s avatar`}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                                <p className="text-sm font-normal text-neutral-800">
                                  {user.first_name} {user.last_name}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div
                                  className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                                    selectedFriends.has(user.user_id)
                                      ? "bg-indigo-600 border-white"
                                      : "bg-white border-neutral-300"
                                  } transition-colors duration-200`}
                                >
                                  {selectedFriends.has(user.user_id) && (
                                    <Check className="w-4 h-2 text-white" />
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}

                        {searchQuery.length >= 3 &&
                          searchResults.length === 0 && (
                            <div className="text-center py-4 text-sm text-gray-500">
                              No users found
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Picks Section */}
          {connections && connections.length > 0 && (
            <div className="space-y-1">
              <Label>Quick Picks</Label>
              <div className="flex gap-2 overflow-x-auto py-2">
                {connections.map(
                  (connection: {
                    user_id: string;
                    name: string;
                    profile_image: string;
                  }) => {
                    return (
                      <button
                        key={connection.user_id}
                        className={`flex flex-col items-center space-y-2 min-w-[72px] relative group`}
                        onClick={() => {
                          toggleFriendSelection(connection.user_id);
                        }}
                      >
                        <div
                          className={`w-12 h-12 rounded-full bg-gradient-to-tr transition-all duration-300 ease-in-out hover:scale-105 ${
                            selectedFriends.has(connection.user_id)
                              ? "from-blue-500 to-indigo-500 scale-105"
                              : "from-blue-200 to-indigo-200 scale-100"
                          } p-[2px]`}
                        >
                          <div className="w-full h-full rounded-full bg-white p-[2px] transition-transform duration-300">
                            <div className="w-full h-full rounded-full overflow-hidden">
                              {connection.profile_image && (
                                <img
                                  src={connection.profile_image}
                                  alt={`${connection.name}'s avatar`}
                                  className="w-full h-full object-cover transition-transform duration-300"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-center truncate w-full font-medium">
                            {connection.name.split(" ")[0]}
                          </span>
                          {connection.name.split(" ")[1] && (
                            <span className="text-xs text-center truncate w-full text-neutral-500">
                              {connection.name.split(" ")[1]}
                            </span>
                          )}
                        </div>
                        <div
                          className={`absolute -top-2 right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                            selectedFriends.has(connection.user_id)
                              ? "bg-indigo-600 border-white"
                              : "bg-white border-neutral-300"
                          } transition-colors duration-200`}
                        >
                          {selectedFriends.has(connection.user_id) && (
                            <Check className="w-4 h-2 text-white" />
                          )}
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          )}
          {/* Button to share trip */}
          <div className="flex justify-center items-center flex-col space-y-3 mt-4">
            <Button
              className={`w-full text-center bg-indigo-600 hover:bg-indigo-700 rounded-xl ${
                selectedFriends.size === 0 ? "opacity-50 cursor-default" : ""
              }`}
              onClick={handleShare}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <span>Share Trip</span>
              )}
            </Button>
            {selectedFriends.size > 0 && (
              <p className="text-xs text-neutral-500 font-light">
                Sharing with {selectedFriends.size} friend
                {selectedFriends.size > 1 ? "s" : ""}
              </p>
            )}
          </div>

          {/* Email Invite Section
            <div className="space-y-2">
              <Label>Invite by Email</Label>
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <div className="relative h-10 w-full">
                    <Mail className="absolute w-4 h-4 left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" />
                    <Input
                      type="text"
                      placeholder="Search friends by name or email"
                      className="text-sm pl-10 pr-3 py-2 rounded-lg focus-visible:ring-0 focus-visible:ring-transparent w-full border border-gray-300 shadow-sm focus:outline-none focus:ring-0 placeholder:font-light"
                    />
                  </div>
                </div>
                <Button
                  // onClick={handleEmailInvite}
                  disabled={!inviteEmail}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <i className="fa-solid fa-paper-plane-top mr-2"></i>
                  Invite
                </Button>
              </div>
            </div> */}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface AddButtonProps {
  tripName: string;
  onAdd?: () => void;
  compact?: boolean;
}

export const AddButton: React.FC<AddButtonProps> = ({
  tripName,
  onAdd,
  compact = false,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();

  const handleAdd = () => {
    setIsAdding(true);
    const { dismiss } = toast({
      title: "Trip Added",
      description: `${tripName} has been added to your trips`,
      duration: 2500,
    });

    if (onAdd) {
      onAdd();
    }

    setTimeout(() => {
      setIsAdding(false);
      dismiss();
    }, 2500);
  };

  return (
    <button
      className={`flex flex-row items-center group relative hover:bg-neutral-100 rounded-full ${
        compact ? "" : "py-2 pl-2 pr-2"
      }`}
      onClick={handleAdd}
    >
      <PlusCircleIcon className="w-5 h-5 absolute text-neutral-500 transition-opacity duration-300 opacity-100 group-hover:opacity-0" />
      {isAdding ? (
        <CheckCircleIconSolid className="w-5 h-5 text-indigo-700 transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
      ) : (
        <PlusCircleIconSolid className="w-5 h-5 text-indigo-700 transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
      )}
      {!compact && (
        <span className="ml-2 font-medium text-neutral-500 text-xs 2xl:text-[13px] group-hover:text-indigo-700 transition-colors duration-300">
          Add Trip
        </span>
      )}
    </button>
  );
};

interface LikeButtonProps {
  className?: string;
  iconClassName?: string;
  showCount?: boolean;
  count?: number;
  userLiked: boolean;
  isLiked: boolean;
  onLike?: () => void;
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  className = "group relative flex items-center flex-row space-x-2",
  iconClassName = "fa-lg",
  showCount = false,
  count = 0,
  userLiked,
  isLiked,
  onLike,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  return (
    <button className={className} onClick={onLike}>
      <div className="relative">
        <HeartIcon
          className={`w-5 absolute transition-opacity duration-300 ${
            isLiked ? "opacity-0" : "opacity-100 group-hover:opacity-0"
          }`}
        />
        <HeartIconSolid
          className={`w-5 text-red-500 transition-opacity duration-300 ${
            isLiked ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          } ${isAnimating ? "animate-pulse" : ""}`}
        />
      </div>

      {showCount && (
        <span className="text-sm group-hover:text-red-500">{count}</span>
      )}
    </button>
  );
};
